import { useEffect, useRef, useState } from "react";
import { homeData } from "../../../mocks/homeData";
import type { AboutData } from "../../../api";

interface Props {
  data?: AboutData | null;
}

export default function AboutSection({ data: apiData }: Props) {
  const about = apiData ? apiData : homeData.about;
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-light-blue py-16 md:py-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Tag */}
        <div className="inline-flex items-center px-4 py-1.5 bg-white rounded-full mb-6">
          <span className="text-dark text-sm font-medium">{about.tag}</span>
        </div>

        {/* Title */}
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-dark leading-tight mb-12">
          Pioneering Wireless
          <br />
          Communication Since 2005
        </h2>

        {/* Description Features */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {about.features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{feature}</p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {about.stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center transition-all duration-700 ${
                animated
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-2">
                {stat.number}
              </div>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}